/**
 * SEO copy for routes wrapped in PublicLayout (plus /documentation for SmartDocsRoute).
 * Keep in sync with `PublicPageMeta` defaults in `components/seo/PublicPageMeta.tsx`.
 *
 * Routes share one preview image unless an entry sets `ogImagePath`.
 */

export type PublicLayoutSeoEntry = {
  title: string
  description: string
  /** Path under `public/` (or absolute URL); defaults in {@link getPublicLayoutSeo} */
  ogImagePath?: string
  noIndex?: boolean
}

export const PUBLIC_LAYOUT_SEO: Record<string, PublicLayoutSeoEntry> = {
  '/': {
    title: 'TCGEasy — Trading Card Inventory Software for Card Stores',
    description:
      'Inventory, automated pricing, Shopify sync, and in-store tools for trading card stores. Pokémon, Magic, One Piece, and more.',
  },
  '/about': {
    title: 'About TCGEasy',
    description: 'Our mission, values, and how we help trading card retailers manage inventory and pricing.',
  },
  '/features': {
    title: 'Features',
    description:
      'Inventory, pricing automation, Shopify sync, analytics, QR codes, and security features for card stores.',
  },
  '/pricing': {
    title: 'Pricing',
    description: 'Transparent subscription plans for TCG retailers — Starter, Basic, Professional, and Enterprise.',
  },
  '/contact': {
    title: 'Contact',
    description: 'Get in touch with TCGEasy for sales, support, or partnership enquiries.',
  },
  '/changelog': {
    title: 'Changelog',
    description: 'The latest updates, improvements, and fixes to TCGEasy.',
  },
  '/shopify-card-stores': {
    title: 'Inventory and Pricing for Shopify Card Stores',
    description:
      'List more singles on Shopify with one stock count, automated market pricing, and orders that flow back. Built for trading card stores.',
  },
  '/in-store-sales': {
    title: 'In-Store Sales and Trade-Ins for Card Shops',
    description:
      'Sell, trade, and consign from the same stock count as your website, with QR labels and customer baskets for your cases.',
  },
  '/supported-games': {
    title: 'Supported Trading Card Games',
    description:
      'TCGEasy supports Pokémon (English and Japanese), Magic: The Gathering, One Piece, Dragon Ball Super, Riftbound, and Cyberpunk.',
  },
  '/verify-email': {
    noIndex: true,
    title: 'Verify your email',
    description: 'Confirm your email address to finish setting up your TCGEasy account.',
  },
  '/privacy': {
    title: 'Privacy Policy',
    description: 'How TCGEasy collects, uses, and protects your personal and business data.',
  },
  '/tos': {
    title: 'Terms of Service',
    description: 'Terms governing use of the TCGEasy trading card inventory platform.',
  },
  '/faq': {
    title: 'FAQ',
    description: 'Answers to common questions about TCGEasy plans, Shopify integration, security, and support.',
  },
  '/documentation': {
    title: 'Documentation',
    description: 'Guides for TCGEasy setup, inventory, Shopify integration, billing, and day-to-day workflows.',
  },
  '/screencast': {
    title: 'Product Demo',
    description: 'A video walkthrough of TCGEasy for trading card stores.',
  },
}

function seoKeyForPath(pathname: string): string {
  if (pathname === '/') return '/'
  return pathname.replace(/\/+$/, '') || '/'
}

const DEFAULT_FALLBACK_SEO: PublicLayoutSeoEntry = {
  title: 'TCGEasy',
  description: 'Smart inventory for trading card retailers.',
}

export function getPublicLayoutSeo(pathname: string): PublicLayoutSeoEntry {
  const key = seoKeyForPath(pathname)
  return PUBLIC_LAYOUT_SEO[key] ?? DEFAULT_FALLBACK_SEO
}
